import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import LoginForm from "./LoginForm";
import { request, requestWithHeaders } from "../beta/utils/fetchData";
import { checkForXSS, safeRedirect } from "../beta/utils/queryStringHelper";

const buildTwoFaSearch = (context) => {
    if (typeof context === "string") {
        return context ? `?${context.replace(/^\?/, "")}` : "";
    }

    const searchParams = new URLSearchParams();

    if (context?.returnUrl != null) {
        searchParams.set("returnUrl", context.returnUrl);
    }

    if (context?.provider != null) {
        searchParams.set("provider", context.provider);
    }

    if (context?.rememberMe != null) {
        searchParams.set("rememberMe", context.rememberMe);
    }

    const query = searchParams.toString();
    return query ? `?${query}` : "";
};

const AuthEntry = () => {
    const [mode, setMode] = useState(); // "login" || "2fa"
    const history = useHistory();
    const location = useLocation();
    const skipTwoFactorRedirect = location.state?.skipTwoFactorRedirect;
    const returnUrlFromState = location.state?.returnUrl;

    const goToVerify2FA = (context) => {
        setMode("2fa");
        history.push(`/Account/Verify2FA${buildTwoFaSearch(context)}`);
    };

    useEffect(() => {
        if (skipTwoFactorRedirect) {
            setMode("login");
            return;
        }

        const checkUserStatus = async () => {
            try {
                const result = await request("/api/account/user-status", "GET");
                if (result.response && result.response.ok) {
                    const data = (result && result.data) || {};
                    if (!data.userId) {
                        setMode("login");
                    } else {
                        goToVerify2FA({
                            returnUrl: "/",
                            rememberMe: false,
                            provider: "Email",
                        });
                    }
                }
            } catch {
                console.log(["Error checking user authentication status"]);
            }
        };

        checkUserStatus();
    }, [skipTwoFactorRedirect]);

    const handleLoginWithUsernameSubmit = (form, helpers) => {
        const { username, password, rememberMe, antiforgeryToken } = form;
        const { setErrors, setRequestInProgress } = helpers;

        setRequestInProgress(true);

        const urlParams = new URLSearchParams(window.location.search);
        const bodyObj = {
            username,
            password,
            rememberMe,
        };
        if (returnUrlFromState) {
            bodyObj.returnUrl = returnUrlFromState;
        } else if (urlParams.has("ReturnUrl")) {
            bodyObj.returnUrl = urlParams.get("ReturnUrl");
        }

        const submitLoginRequest = async () => {
            try {
                const result = await requestWithHeaders(
                    "/api/account/login",
                    "POST",
                    bodyObj,
                    {
                        "X-XSRF-TOKEN": antiforgeryToken,
                    }
                );
                if (result.response && result.response.ok) {
                    const data = result.data || {};

                    if (data.value?.result?.requiresTwoFactor) {
                        goToVerify2FA(data.value.returnUrl);
                        return;
                    }

                    localStorage.setItem("username", username);
                    localStorage.setItem(
                        "daysLeftForPasswordExpiration",
                        data?.value?.daysLeftForPasswordExpiration
                    );

                    const redirectUrl = checkForXSS(data.value?.returnUrl);
                    safeRedirect(redirectUrl);
                } else {
                    const data =
                        (result && result.error && result.error.data) || {};
                    setErrors(data.errors || ["Login failed."]);
                }
            } catch {
                setErrors(["Login failed."]);
            } finally {
                setRequestInProgress(false);
            }
        };

        submitLoginRequest();
    };

    return (
        <>
            {mode !== "2fa" && (
                <LoginForm onSubmit={handleLoginWithUsernameSubmit} />
            )}
        </>
    );
};

export default AuthEntry;
